import React, { FC, useState, useEffect, useRef, useMemo } from 'react';

import styled from 'styled-components';

import theme from '@adsk/alloy-react-theme';
import Tooltip, { OverflowTooltip } from '@adsk/alloy-react-tooltip';

import { CategoryMetadata } from './types';

const Category = styled.span(theme.typography.bodyMedium);
const OverflowContainer = styled(Category)(theme.typography.ellipsis);

type TruncationLevel = 'none' | 'middle' | 'ancestors';

type CategoryNameProps = {
  category: CategoryMetadata;
};

const CategoryName: FC<CategoryNameProps> = ({ category: { name, path } }) => {
  const nameRef = useRef<HTMLDivElement>(null);
  const [currentTruncationLevel, setCurrentTruncationLevel] = useState<
    TruncationLevel | undefined
  >('none');
  const width = useMemo(() => nameRef?.current?.offsetWidth, [nameRef]);

  const pathWithoutRoot = path.slice(1);

  const fullPath = pathWithoutRoot
    .map(({ name }) => name)
    .concat([name])
    .join(' > ');

  useEffect(() => {
    setCurrentTruncationLevel('none');
  }, [fullPath]);

  useEffect(() => {
    const element = nameRef.current;

    if (
      !element ||
      !element.parentElement ||
      element.parentElement.offsetWidth >= element.offsetWidth
    ) {
      return;
    }

    // the element wants to be larger than it can be, so increase the truncation level
    setCurrentTruncationLevel((currentTruncationLevel) =>
      currentTruncationLevel === 'none' ? 'middle' : 'ancestors',
    );
  }, [width]);

  if (pathWithoutRoot.length <= 1 || currentTruncationLevel === 'none') {
    return (
      <OverflowTooltip content={fullPath} singleLine>
        <Category ref={nameRef}>{fullPath}</Category>
      </OverflowTooltip>
    );
  }

  return (
    <Tooltip content={fullPath} shouldWrapChildren={false}>
      <OverflowContainer>
        <Category ref={nameRef}>
          {currentTruncationLevel === 'middle'
            ? `${pathWithoutRoot[0].name} > \u2026 > ${name}`
            : `\u2026 > ${name}`}
        </Category>
      </OverflowContainer>
    </Tooltip>
  );
};

export default CategoryName;
